import { useActiveContext } from "@/contexts/ActiveContextContext";
import { useAuth } from "@/contexts/AuthContext";
import postsService from "@/services/postsService";
import {
  showToastError,
  showToastSuccess,
  showToastWarning,
} from "@/shared/alertService";
import apiService from "@/shared/apiService";
import { useTranslation } from "@/contexts/LanguageContext";
import { getPlatformName } from "@/utils/platformIcons";
import { FileText, Layout, ShieldCheck, Target, Users } from "lucide-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";

// --- Types & Interfaces ---
export type Platform =
  | "meta"
  | "google_ads"
  | "tiktok"
  | "linkedin"
  | "chatgpt_ads";
export type ChatGptAdsObjective = "clicks" | "conversions";
export type MetaPlatform = "facebook" | "instagram" | "both";
export type GoogleCampaignType =
  | "search"
  | "display"
  | "video"
  | "shopping"
  | "performance_max";
export type DestinationType =
  | "website"
  | "whatsapp"
  | "form"
  | "messenger"
  | "instagram_direct"
  | "calls";
export type PerformanceGoal =
  | "CONVERSATIONS"
  | "LANDING_PAGE_VIEWS"
  | "LINK_CLICKS"
  | "REACH"
  | "QUALITY_CALL";
export type MetaLookalikeSource =
  | "b2c_qualified_leads"
  | "b2b_customers"
  | "pixel_event"
  | "crm_upload";
export type GoogleCustomerMatchSource =
  | "b2c_qualified_leads"
  | "b2b_customers"
  | "crm_upload";

export interface CustomField {
  id: string;
  label: string;
  type: "text" | "textarea" | "select" | "number" | "email" | "tel";
  required: boolean;
  options?: Array<{ value: string; label: string }>;
}

export interface ApprovedPost {
  id: number;
  user_id: number;
  content: string;
  platform: string;
  status: string;
  scheduled_date?: string;
  published_at?: string;
  hashtags?: string | null;
  call_to_action?: string | null;
  theme?: string | null;
  image_info?: any;
  video_info?: any;
  media_gallery?: any[];
  linkedin_post_id?: string;
  created_at?: string;
}

export interface Sitelink {
  title: string;
  description1: string;
  description2: string;
  finalUrl: string;
}

import { logger } from "@/utils/logger";
import {
  extractFirstUrl,
  extractPostMediaUrl,
  extractPostMediaUrls,
  extractWhatsappPhoneFromUrl,
  formatDateYMD,
  isWhatsappUrl,
  suggestAdContentFromPost,
} from "../utils/adsUtils";
import {
  AD_TEXT_LIMITS,
  CAMPAIGN_NAME_LIMITS,
  CUSTOM_QUESTION_LABEL_LIMIT,
  GOOGLE_ADS_BUSINESS_NAME_LIMIT,
  LINKEDIN_LEAD_FORM_LIMITS,
} from "../utils/charLimits";

// --- Context Definition ---
interface AdsContextType {
  // Platform & Step
  platform: Platform;
  setPlatform: (p: Platform) => void;
  currentStep: number;
  setCurrentStep: (s: number) => void;
  steps: any[];
  nextStep: () => void;
  prevStep: () => void;
  isLastStep: boolean;
  canGoNext: () => boolean;
  charLimitIssues: { label: string; current: number; max: number }[];
  missingFieldIssues: string[];

  // Core Data
  name: string;
  setName: (v: string) => void;
  dailyBudget: string;
  setDailyBudget: (v: string) => void;
  budgetMode: "ABO" | "CBO";
  setBudgetMode: (v: "ABO" | "CBO") => void;
  costPerResultGoal: string;
  setCostPerResultGoal: (v: string) => void;
  linkUrl: string;
  setLinkUrl: (v: string) => void;
  message: string;
  setMessage: (v: string) => void;
  headline: string;
  setHeadline: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  ctaText: string;
  setCtaText: (v: string) => void;

  // Destination
  destinationType: DestinationType;
  setDestinationType: (v: DestinationType) => void;
  performanceGoal: PerformanceGoal;
  setPerformanceGoal: (v: PerformanceGoal) => void;
  whatsappPhone: string;
  setWhatsappPhone: (v: string) => void;
  whatsappPrefill: string;
  setWhatsappPrefill: (v: string) => void;
  whatsappOfficial: boolean;
  setWhatsappOfficial: (v: boolean) => void;
  computedWhatsappUrl: string;
  privacyPolicyUrl: string;
  setPrivacyPolicyUrl: (v: string) => void;

  // Dates & Times
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  startTime: string;
  setStartTime: (v: string) => void;
  endTime: string;
  setEndTime: (v: string) => void;

  // Targeting
  ageMin: string;
  setAgeMin: (v: string) => void;
  ageMax: string;
  setAgeMax: (v: string) => void;
  gender: "all" | "male" | "female";
  setGender: (v: "all" | "male" | "female") => void;
  country: string;
  setCountry: (v: string) => void;
  states: string[];
  setStates: (v: string[]) => void;
  cities: any[];
  setCities: (v: any[]) => void;

  // Meta Specific
  metaPlatform: MetaPlatform;
  setMetaPlatform: (v: MetaPlatform) => void;
  interests: Array<{ id: string; name: string }>;
  setInterests: (v: any[]) => void;
  radiusKm: string;
  setRadiusKm: (v: string) => void;
  customLocations: any[];
  setCustomLocations: (v: any[]) => void;
  searchTerms: string;
  setSearchTerms: (v: string) => void;
  geocodeResults: any[];
  setGeocodeResults: (v: any[]) => void;
  searchingGeocode: boolean;
  handleGeocodeSearch: () => Promise<void>;
  addCustomLocation: (res: any) => void;
  businessName: string;
  setBusinessName: (v: string) => void;
  youtubeUrls: string[];
  setYoutubeUrls: (v: string[]) => void;

  // Google Specific
  googleCampaignType: GoogleCampaignType;
  setGoogleCampaignType: (v: GoogleCampaignType) => void;
  locations: Array<{ type: string; value: string }>;
  setLocations: (v: any[]) => void;
  keywords: string[];
  setKeywords: (v: string[]) => void;
  addKeyword: (v?: string) => void;
  removeKeyword: (idx: number) => void;
  trendsLoading: boolean;
  optimizeKeywordsWithTrends: () => Promise<void>;
  googlePmaxImageUrl: string;
  setGooglePmaxImageUrl: (v: string) => void;
  sitelinks: Sitelink[];
  setSitelinks: (v: Sitelink[]) => void;
  updateSitelink: (idx: number, updates: Partial<Sitelink>) => void;

  // Advanced Targeting
  useMetaLookalike: boolean;
  setUseMetaLookalike: (v: boolean) => void;
  metaLookalikeSource: MetaLookalikeSource;
  setMetaLookalikeSource: (v: MetaLookalikeSource) => void;
  metaLookalikePercent: "1" | "2" | "5";
  setMetaLookalikePercent: (v: "1" | "2" | "5") => void;
  metaLookalikeEvent: string;
  setMetaLookalikeEvent: (v: string) => void;
  metaLookalikeAudienceId: string;
  creatingLookalike: boolean;
  createMetaLookalike: () => Promise<void>;
  useMetaCustomAudience: boolean;
  setUseMetaCustomAudience: (v: boolean) => void;
  metaCustomAudienceName: string;
  setMetaCustomAudienceName: (v: string) => void;
  metaCustomAudienceId: string;
  setMetaCustomAudienceId: (v: string) => void;
  metaCustomAudiencesList: {
    id: string;
    name: string;
    approximate_count: number;
  }[];
  fetchingCustomAudiences: boolean;
  fetchMetaCustomAudiences: () => Promise<void>;
  creatingCustomAudience: boolean;
  createMetaCustomAudience: () => Promise<void>;
  useGoogleCustomerMatch: boolean;
  setUseGoogleCustomerMatch: (v: boolean) => void;
  googleCustomerMatchSource: GoogleCustomerMatchSource;
  setGoogleCustomerMatchSource: (v: GoogleCustomerMatchSource) => void;
  googleCustomerMatchUserListResource: string;
  creatingCustomerMatch: boolean;
  createGoogleCustomerMatch: () => Promise<void>;

  // LinkedIn Specific
  linkedinAccounts: any[];
  loadingLinkedinAccounts: boolean;
  loadLinkedinAccounts: (force?: boolean) => Promise<void>;
  selectedLinkedinAccount: string;
  setSelectedLinkedinAccount: (v: string) => void;
  linkedinJobTitles: Array<{ urn: string; name: string }>;
  setLinkedinJobTitles: (v: Array<{ urn: string; name: string }>) => void;
  linkedinIndustries: Array<{ urn: string; name: string }>;
  setLinkedinIndustries: (v: Array<{ urn: string; name: string }>) => void;
  linkedinSeniorities: Array<{ urn: string; name: string }>;
  setLinkedinSeniorities: (v: Array<{ urn: string; name: string }>) => void;
  linkedinCompanySizes: Array<{ urn: string; name: string }>;
  setLinkedinCompanySizes: (v: Array<{ urn: string; name: string }>) => void;
  linkedinSkills: Array<{ urn: string; name: string }>;
  setLinkedinSkills: (v: Array<{ urn: string; name: string }>) => void;
  linkedinJobFunctions: Array<{ urn: string; name: string }>;
  setLinkedinJobFunctions: (v: Array<{ urn: string; name: string }>) => void;
  linkedinMatchedAudiences: Array<{ urn: string; name: string; type?: string }>;
  setLinkedinMatchedAudiences: (
    v: Array<{ urn: string; name: string; type?: string }>,
  ) => void;
  trackingParam: string;
  setTrackingParam: (v: string) => void;
  isTrackingParamManual: boolean;
  setIsTrackingParamManual: (v: boolean) => void;
  linkedinLeadFormUrn: string;
  setLinkedinLeadFormUrn: (v: string) => void;
  linkedinFormName: string;
  setLinkedinFormName: (v: string) => void;
  linkedinFormCta: string;
  setLinkedinFormCta: (v: string) => void;
  linkedinFormHeadline: string;
  setLinkedinFormHeadline: (v: string) => void;
  linkedinFormDescription: string;
  setLinkedinFormDescription: (v: string) => void;

  // ChatGPT Ads Specific (targeting é por CONTEXTO de conversa, não demografia)
  contextHints: string[];
  setContextHints: (v: string[]) => void;
  addContextHint: (v?: string) => void;
  removeContextHint: (idx: number) => void;
  chatgptObjective: ChatGptAdsObjective;
  setChatgptObjective: (v: ChatGptAdsObjective) => void;

  // Posts & Base Ad
  approvedPosts: ApprovedPost[];
  approvedPostsLoading: boolean;
  approvedPostId: string;
  setApprovedPostId: (v: string) => void;
  selectedApprovedPost: ApprovedPost | null;
  loadApprovedPosts: (force?: boolean) => Promise<void>;
  applyApprovedPostToAd: (post?: ApprovedPost) => void;

  // Leads
  leadFields: string[];
  setLeadFields: (v: string[]) => void;
  standardLeadFields: any[];
  customFields: CustomField[];
  setCustomFields: (v: CustomField[]) => void;
  addCustomField: () => void;
  updateCustomField: (idx: number, updates: Partial<CustomField>) => void;
  removeCustomField: (idx: number) => void;

  // State/Loading
  loading: boolean;
  suggesting: boolean;
  setSuggesting: (v: boolean) => void;
  handleSuggestTargeting: () => Promise<void>;
  applySuggestedTargeting: () => void;
  suggestedTargeting: any;
  handleLaunchCampaign: (e?: any) => Promise<void>;
  handleSaveDraft: () => Promise<void>;
  draftId: string | null;
  postMediaUrls: { url: string; type: "image" | "video"; thumbnail: string }[];
  setPostMediaUrls: (
    v: { url: string; type: "image" | "video"; thumbnail: string }[],
  ) => void;

  // Estimativa de Resultados
  reachEstimate: any;
  loadingEstimate: boolean;
  reachEstimateError: boolean;
  linkedinLanguage: string;
  setLinkedinLanguage: (v: string) => void;
  linkedinObjective: string;
  setLinkedinObjective: (v: string) => void;
}

const AdsContext = createContext<AdsContextType | undefined>(undefined);

export const AdsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { activeContext } = useActiveContext();
  const { user } = useAuth();
  const { t } = useTranslation();
  const MIN_META_DAILY_BUDGET_BRL = 5.33;
  // Orçamento mínimo diário observado no próprio Ads Manager da OpenAI
  // (ads.openai.com) — a Advertiser API só aceita orçamento TOTAL (lifetime,
  // mínimo técnico $10 = 1.000.000 micros), sem orçamento diário recorrente;
  // por isso o valor diário informado aqui é multiplicado pelos dias do
  // período pra virar o total enviado, mas o piso de $25/dia é validado
  // sobre o valor diário em si (é o que a OpenAI recomenda pra veiculação
  // funcionar de verdade, mesmo o mínimo técnico da API sendo menor).
  const MIN_CHATGPT_DAILY_BUDGET_USD = 25;

  // Evita setState em efeitos assíncronos (posts, contas LinkedIn) que resolvem
  // depois que o usuário já saiu da página de Ads. Reseta para `true` DENTRO
  // do efeito (não só no useRef inicial) porque em StrictMode (dev) o React
  // monta, roda o cleanup simulando um desmonte e monta de novo — sem esse
  // reset aqui, o cleanup zera `isMountedRef.current` nessa simulação e nada
  // volta a marcar `true`, fazendo toda leitura subsequente crer que o
  // provider está desmontado para sempre (era a causa do loading preso).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // --- State Hooks ---
  const [platform, setPlatform] = useState<Platform>("meta");
  const [metaPlatform, setMetaPlatform] = useState<MetaPlatform>("both");
  const [useMetaCustomAudience, setUseMetaCustomAudience] = useState(false);
  const [metaCustomAudienceName, setMetaCustomAudienceName] = useState(
    t("Público Personalizado (CRM)"),
  );
  const [metaCustomAudienceId, setMetaCustomAudienceId] = useState("");
  const [metaCustomAudiencesList, setMetaCustomAudiencesList] = useState<
    { id: string; name: string; approximate_count: number }[]
  >([]);
  const [fetchingCustomAudiences, setFetchingCustomAudiences] = useState(false);
  const [creatingCustomAudience, setCreatingCustomAudience] = useState(false);
  const [googleCampaignType, setGoogleCampaignType] =
    useState<GoogleCampaignType>("search");
  const [name, setName] = useState("");
  const [dailyBudget, setDailyBudget] = useState("");
  const [budgetMode, setBudgetMode] = useState<"ABO" | "CBO">("ABO");
  const [costPerResultGoal, setCostPerResultGoal] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [youtubeUrls, setYoutubeUrls] = useState<string[]>([""]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [postMediaUrls, setPostMediaUrls] = useState<
    { url: string; type: "image" | "video"; thumbnail: string }[]
  >([]);

  const [message, setMessage] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [ctaText, setCtaText] = useState("LEARN_MORE");
  const [trackingParam, setTrackingParam] = useState("");
  const [isTrackingParamManual, setIsTrackingParamManual] = useState(false);
  const [destinationType, setDestinationType] =
    useState<DestinationType>("website");
  const [performanceGoal, setPerformanceGoal] =
    useState<PerformanceGoal>("LINK_CLICKS");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappPrefill, setWhatsappPrefill] = useState(
    t("Olá! Tenho interesse."),
  );
  const [whatsappOfficial, setWhatsappOfficial] = useState(false);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");

  // Auto-fill context data (Identity & Destination)
  useEffect(() => {
    if (!draftId) {
      // 1. Business Name (Always try to fill if empty)
      if (!businessName) {
        const autoName =
          activeContext.clientCompany?.name ||
          activeContext.organization?.name ||
          "";
        if (autoName) setBusinessName(autoName);
      }

      // 2. WhatsApp (When destination is whatsapp)
      if (!whatsappPhone && destinationType === "whatsapp") {
        const autoPhone =
          activeContext.clientCompany?.phone ||
          activeContext.organization?.phone ||
          "";
        if (autoPhone) setWhatsappPhone(autoPhone);
      }

      // 3. Privacy Policy (When destination is form or platform is linkedin)
      if (
        !privacyPolicyUrl &&
        (destinationType === "form" || platform === "linkedin")
      ) {
        const autoPrivacy =
          activeContext.clientCompany?.privacy_policy_url ||
          activeContext.organization?.privacy_policy_url ||
          "";
        if (autoPrivacy) setPrivacyPolicyUrl(autoPrivacy);
      }

      // 4. Website Link (When destination is website)
      if (!linkUrl && destinationType === "website") {
        const autoWeb =
          activeContext.clientCompany?.website_url ||
          activeContext.organization?.website_url ||
          "";
        if (autoWeb) setLinkUrl(autoWeb);
      }
    } // Fim do if (!draftId)

    // 5. URL Tracking (UTM) - Roda independentemente do draftId para preencher campanhas antigas que não tinham UTM
    if (isTrackingParamManual) return;

    const clientName = (
      activeContext.clientCompany?.name ||
      activeContext.organization?.name ||
      ""
    )
      .trim()
      .replace(/\s+/g, "_")
      .toLowerCase();

    const campaignNameStr = name
      ? name.trim().replace(/\s+/g, "_").toLowerCase()
      : "";

    let newTrackingParam = "";

    if (platform === "meta") {
      const utmSource = encodeURIComponent(clientName || "meta");
      const utmCampaign = encodeURIComponent(campaignNameStr || "campanha");
      const utmContent = encodeURIComponent(clientName || "ad");
      newTrackingParam = `utm_source=${utmSource}&utm_medium=cpc&utm_campaign=${utmCampaign}&utm_content=${utmContent}`;
    } else {
      let source = "meta";
      if (platform === "linkedin") source = "linkedin";
      else if (platform === "google_ads") source = "google";
      else if (platform === "tiktok") source = "tiktok";

      let utm = `utm_source=${clientName ? encodeURIComponent(clientName) : source}&utm_medium=paid_social`;
      if (campaignNameStr)
        utm += `&utm_campaign=${encodeURIComponent(campaignNameStr)}`;
      if (clientName) utm += `&utm_content=${encodeURIComponent(clientName)}`;
      newTrackingParam = utm;
    }

    setTrackingParam((prev) => {
      if (prev === newTrackingParam) return prev;
      return newTrackingParam;
    });
  }, [activeContext, platform, name, isTrackingParamManual]);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return formatDateYMD(d);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 8);
    return formatDateYMD(d);
  });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("23:59");

  const [ageMin, setAgeMin] = useState("25");
  const [ageMax, setAgeMax] = useState("45");
  const [gender, setGender] = useState<"all" | "male" | "female">("all");
  const [country, setCountry] = useState("BR");
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const [interests, setInterests] = useState<any[]>([]);
  const [radiusKm, setRadiusKm] = useState("40");
  const [customLocations, setCustomLocations] = useState<any[]>([]);
  const [searchTerms, setSearchTerms] = useState("");
  const [geocodeResults, setGeocodeResults] = useState<any[]>([]);
  const [searchingGeocode, setSearchingGeocode] = useState(false);

  const [locations, setLocations] = useState<any[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [googlePmaxImageUrl, setGooglePmaxImageUrl] = useState("");

  // LinkedIn Specific State
  const [linkedinAccounts, setLinkedinAccounts] = useState<any[]>([]);
  const [loadingLinkedinAccounts, setLoadingLinkedinAccounts] = useState(false);
  const [selectedLinkedinAccount, setSelectedLinkedinAccount] = useState("");
  const [linkedinJobTitles, setLinkedinJobTitles] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinIndustries, setLinkedinIndustries] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinSeniorities, setLinkedinSeniorities] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinCompanySizes, setLinkedinCompanySizes] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinSkills, setLinkedinSkills] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinJobFunctions, setLinkedinJobFunctions] = useState<
    Array<{ urn: string; name: string }>
  >([]);
  const [linkedinMatchedAudiences, setLinkedinMatchedAudiences] = useState<
    Array<{ urn: string; name: string; type?: string }>
  >([]);
  const [linkedinLeadFormUrn, setLinkedinLeadFormUrn] = useState("");
  const [linkedinFormName, setLinkedinFormName] = useState("");
  const [linkedinFormCta, setLinkedinFormCta] = useState("SIGN_UP");
  const [linkedinFormHeadline, setLinkedinFormHeadline] = useState("");
  const [linkedinFormDescription, setLinkedinFormDescription] = useState("");
  const [linkedinLanguage, setLinkedinLanguage] = useState("pt_BR");
  const [linkedinObjective, setLinkedinObjective] = useState("awareness");
  const [reachEstimate, setReachEstimate] = useState<any>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [reachEstimateError, setReachEstimateError] = useState(false);

  // ChatGPT Ads Specific
  const [contextHints, setContextHints] = useState<string[]>([]);
  const [chatgptObjective, setChatgptObjective] =
    useState<ChatGptAdsObjective>("clicks");
  const addContextHint = (value?: string) => {
    const hint = (value || "").trim();
    if (hint && !contextHints.includes(hint))
      setContextHints([...contextHints, hint]);
  };
  const removeContextHint = (idx: number) =>
    setContextHints(contextHints.filter((_, i) => i !== idx));

  const [sitelinks, setSitelinks] = useState<Sitelink[]>([
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
  ]);

  const updateSitelink = (idx: number, updates: Partial<Sitelink>) => {
    const newSitelinks = [...sitelinks];
    newSitelinks[idx] = { ...newSitelinks[idx], ...updates };
    setSitelinks(newSitelinks);
  };

  const standardLeadFields = [
    { id: "name", label: "Nome completo", required: true },
    { id: "email", label: "E-mail", required: true },
    { id: "phone", label: "Telefone", required: false },
    { id: "cpf", label: "CPF", required: false },
    { id: "company", label: "Empresa", required: false },
    { id: "job_title", label: "Cargo", required: false },
    { id: "budget", label: "Orçamento estimado", required: false },
    { id: "industry", label: "Setor/Indústria", required: false },
    { id: "website", label: "Website", required: false },
  ];
  const [leadFields, setLeadFields] = useState<string[]>(["name", "email"]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  // `loading` só deve ficar `true` no boot quando há um rascunho para carregar
  // via `draftId` na URL — nesse caso o efeito de carregamento do rascunho
  // (mais abaixo) o resolve. Para uma campanha nova não há nada bloqueante
  // para renderizar o Step 1, então já nasce `false` (dados secundários como
  // posts aprovados/publicados e contas LinkedIn são carregados sob demanda
  // por etapa e não devem prender o skeleton inicial do wizard).
  const [loading, setLoading] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!new URLSearchParams(window.location.search).get("draftId");
  });
  const [suggesting, setSuggesting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [suggestedTargeting, setSuggestedTargeting] = useState<any>(null);

  const [useMetaLookalike, setUseMetaLookalike] = useState(false);
  const [metaLookalikeSource, setMetaLookalikeSource] =
    useState<MetaLookalikeSource>("b2c_qualified_leads");
  const [metaLookalikePercent, setMetaLookalikePercent] = useState<
    "1" | "2" | "5"
  >("1");
  const [metaLookalikeEvent, setMetaLookalikeEvent] = useState("Lead");
  const [metaLookalikeAudienceId, setMetaLookalikeAudienceId] = useState("");
  const [creatingLookalike, setCreatingLookalike] = useState(false);

  const [useGoogleCustomerMatch, setUseGoogleCustomerMatch] = useState(false);
  const [googleCustomerMatchSource, setGoogleCustomerMatchSource] =
    useState<GoogleCustomerMatchSource>("b2c_qualified_leads");
  const [
    googleCustomerMatchUserListResource,
    setGoogleCustomerMatchUserListResource,
  ] = useState("");
  const [creatingCustomerMatch, setCreatingCustomerMatch] = useState(false);

  const [approvedPosts, setApprovedPosts] = useState<ApprovedPost[]>([]);
  const [approvedPostsLoading, setApprovedPostsLoading] = useState(false);
  const [approvedPostId, setApprovedPostId] = useState<string>("");

  // --- Memos & Refs ---
  const steps = useMemo(() => {
    const base = [
      {
        id: 1,
        label: t("Plataforma"),
        icon: Layout,
        description: t("Onde seu anúncio vai aparecer"),
      },
      {
        id: 2,
        label: t("Conteúdo"),
        icon: FileText,
        description: t("O que o usuário vai ver"),
      },
      {
        id: 3,
        label: t("Público"),
        icon: Target,
        description: t("Quem deve ser impactado"),
      },
    ];

    let currentId = 4;
    if (
      destinationType === "form" ||
      (platform === "linkedin" && linkedinObjective === "leads")
    ) {
      base.push({
        id: currentId++,
        label: t("Leads"),
        icon: Users,
        description: t("Configuração do formulário"),
      });
    }

    base.push({
      id: currentId,
      label: t("Revisão"),
      icon: ShieldCheck,
      description: t("Confirmação final"),
    });

    return base;
  }, [destinationType, platform, linkedinObjective, t]);

  const isLastStep = useMemo(() => {
    const hasExtraStep =
      destinationType === "form" ||
      (platform === "linkedin" && linkedinObjective === "leads");
    const lastId = hasExtraStep ? 5 : 4;
    return currentStep === lastId;
  }, [destinationType, currentStep, platform, linkedinObjective]);

  const computedWhatsappUrl = useMemo(() => {
    const digits = (whatsappPhone || "").replace(/\D/g, "");
    if (!digits) return "";
    return `https://wa.me/${digits}${whatsappPrefill ? `?text=${encodeURIComponent(whatsappPrefill)}` : ""}`;
  }, [whatsappPhone, whatsappPrefill]);

  // Campos com excesso de caracteres na etapa atual — usado tanto para
  // bloquear o avanço em `canGoNext` quanto para explicar ao usuário, na UI,
  // por que o botão "Próximo Passo" está desabilitado.
  const charLimitIssues = useMemo(() => {
    const issues: { label: string; current: number; max: number }[] = [];
    if (currentStep === 1) {
      const nameLimit = CAMPAIGN_NAME_LIMITS[platform];
      if (nameLimit && name.length > nameLimit) {
        issues.push({
          label: t("Nome da Campanha"),
          current: name.length,
          max: nameLimit,
        });
      }
      if (
        platform === "google_ads" &&
        businessName.length > GOOGLE_ADS_BUSINESS_NAME_LIMIT
      ) {
        issues.push({
          label: t("Nome da Empresa (Google Ads)"),
          current: businessName.length,
          max: GOOGLE_ADS_BUSINESS_NAME_LIMIT,
        });
      }
    }
    if (currentStep === 2) {
      // Só o limite técnico (`hard`) bloqueia o avanço — o `recommended`
      // (ex: 125 caracteres da Meta) é só um limite de exibição que a
      // plataforma trunca, não rejeita, então fica de fora daqui (o
      // `CharCounter` já sinaliza em âmbar, sem bloquear).
      const limits = AD_TEXT_LIMITS[platform];
      const messageHard = limits?.message?.hard;
      if (messageHard && message.length > messageHard) {
        issues.push({
          label: t("Copy Principal"),
          current: message.length,
          max: messageHard,
        });
      }
      const headlineHard = limits?.headline?.hard;
      if (headlineHard && headline.length > headlineHard) {
        issues.push({
          label: t("Título de Destaque"),
          current: headline.length,
          max: headlineHard,
        });
      }
      const descriptionHard = limits?.description?.hard;
      if (descriptionHard && description.length > descriptionHard) {
        issues.push({
          label: t("Subtítulo Descritivo"),
          current: description.length,
          max: descriptionHard,
        });
      }
    }
    if (currentStep === 4) {
      customFields.forEach((f, i) => {
        if (f.label.length > CUSTOM_QUESTION_LABEL_LIMIT) {
          issues.push({
            label: t("Pergunta Customizada #{n}", { n: i + 1 }),
            current: f.label.length,
            max: CUSTOM_QUESTION_LABEL_LIMIT,
          });
        }
      });
      if (platform === "linkedin" && linkedinObjective === "leads") {
        if (linkedinFormName.length > LINKEDIN_LEAD_FORM_LIMITS.formName) {
          issues.push({
            label: t("Nome do Formulário"),
            current: linkedinFormName.length,
            max: LINKEDIN_LEAD_FORM_LIMITS.formName,
          });
        }
        if (linkedinFormHeadline.length > LINKEDIN_LEAD_FORM_LIMITS.headline) {
          issues.push({
            label: t("Título do Formulário"),
            current: linkedinFormHeadline.length,
            max: LINKEDIN_LEAD_FORM_LIMITS.headline,
          });
        }
        if (
          linkedinFormDescription.length > LINKEDIN_LEAD_FORM_LIMITS.description
        ) {
          issues.push({
            label: t("Descrição do Formulário"),
            current: linkedinFormDescription.length,
            max: LINKEDIN_LEAD_FORM_LIMITS.description,
          });
        }
      }
    }
    return issues;
  }, [
    currentStep,
    platform,
    name,
    businessName,
    message,
    headline,
    description,
    customFields,
    linkedinObjective,
    linkedinFormName,
    linkedinFormHeadline,
    linkedinFormDescription,
    t,
  ]);

  // Campos obrigatórios não preenchidos na etapa atual — mesma ideia do
  // `charLimitIssues`, mas para o motivo mais comum de o botão "Próximo
  // Passo" estar desabilitado: algo obrigatório ainda vazio.
  const missingFieldIssues = useMemo(() => {
    const issues: string[] = [];
    if (currentStep === 1) {
      if (!platform) issues.push(t("Selecione uma plataforma de veiculação."));
      if (!name.trim()) issues.push(t("Preencha o Nome da Campanha."));
    }
    if (currentStep === 2) {
      const hasMedia = !!approvedPostId || !!linkUrl.trim();
      if (platform === "meta") {
        if (!message.trim()) issues.push(t("Preencha a Copy Principal."));
      } else if (platform === "chatgpt_ads") {
        // O card do ChatGPT sempre exige corpo — título cai pro nome da
        // campanha se vazio, mas o corpo (message) não tem fallback no backend.
        if (!message.trim())
          issues.push(t("Preencha o Corpo do Anúncio (Chat Card)."));
      } else if (!headline.trim() && !message.trim()) {
        issues.push(t("Preencha o Título de Destaque ou a Copy Principal."));
      }
      if (!hasMedia) {
        issues.push(
          t("Selecione um post aprovado ou informe a URL / Link de Destino."),
        );
      }
    }
    if (currentStep === 3) {
      const cleanedBudget = String(dailyBudget)
        .replace(/[^\d.,]/g, "")
        .replace(",", ".");
      const dailyBudgetNum = parseFloat(cleanedBudget);
      if (!(dailyBudgetNum > 0)) {
        issues.push(t("Informe o Investimento Diário."));
      }
      if (!startDate || !endDate) {
        issues.push(t("Selecione o Período (data de início e fim)."));
      } else if (new Date(endDate) < new Date(startDate)) {
        issues.push(t("A data de fim não pode ser anterior à data de início."));
      }
      if (!startTime || !endTime) {
        issues.push(t("Informe o Horário de Início e Fim."));
      }
      if (platform === "chatgpt_ads" && contextHints.length === 0) {
        issues.push(
          t("Adicione ao menos uma palavra-chave de contexto (context hint)."),
        );
      }
    }
    if (currentStep === 4) {
      if (platform === "linkedin" && linkedinObjective === "leads") {
        if (!linkedinLeadFormUrn.trim() && !linkedinFormName.trim()) {
          issues.push(
            t(
              "Informe o Nome do Formulário ou cole o URN de um formulário existente.",
            ),
          );
        }
      } else if (destinationType === "form") {
        if (!privacyPolicyUrl.trim()) {
          issues.push(t("Informe a URL da Página de Privacidade."));
        }
        const hasFields = leadFields.length > 0 || customFields.length > 0;
        if (!hasFields) {
          issues.push(t("Selecione ao menos um campo de captura de leads."));
        }
        const customFieldsValid = customFields.every(
          (f) => f.label.trim().length > 0,
        );
        if (!customFieldsValid) {
          issues.push(
            t("Preencha o texto de todas as perguntas customizadas."),
          );
        }
      }
    }
    return issues;
  }, [
    currentStep,
    platform,
    name,
    message,
    headline,
    linkUrl,
    approvedPostId,
    dailyBudget,
    startDate,
    endDate,
    startTime,
    endTime,
    destinationType,
    privacyPolicyUrl,
    leadFields,
    customFields,
    linkedinObjective,
    linkedinLeadFormUrn,
    linkedinFormName,
    contextHints,
    t,
  ]);

  const canGoNext = useCallback(() => {
    return missingFieldIssues.length === 0 && charLimitIssues.length === 0;
  }, [missingFieldIssues, charLimitIssues]);

  const nextStep = useCallback(() => {
    const hasExtraStep =
      destinationType === "form" ||
      (platform === "linkedin" && linkedinObjective === "leads");
    const lastStepNum = hasExtraStep ? 5 : 4;
    if (currentStep < lastStepNum && canGoNext())
      setCurrentStep(currentStep + 1);
  }, [currentStep, destinationType, platform, linkedinObjective, canGoNext]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  // --- Data Loading ---
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const contextIdRef = useRef(contextId);
  contextIdRef.current = contextId;

  // Posts aprovados/publicados só são usados no Step 2 (hub de importação) e
  // no resumo do Step 4 — não devem ser buscados no mount do wizard nem
  // travar o Step 1. `approvedPostsLoadedContextRef` guarda para qual
  // contexto os dados em `approvedPosts` são válidos, permitindo que
  // `loadApprovedPosts` seja chamado livremente (ex: no mount do Step 2, ou
  // ao voltar/avançar entre etapas) sem gerar requests duplicados — o
  // `postsService`/`SmartCache` também cacheia por contexto+status+paginação,
  // então esse ref evita até o trabalho de checar o cache de novo.
  const approvedPostsLoadedContextRef = useRef<string | null>(null);
  // Requisição em voo (por contexto), pra prefetch do Step 1 e mount do
  // Step 2 não dispararem a mesma busca em paralelo.
  const approvedPostsRequestRef = useRef<{
    contextId: string;
    promise: Promise<void>;
  } | null>(null);
  // Contador monotônico: cada chamada que efetivamente dispara um fetch
  // recebe o próximo número. Só a chamada cujo número ainda é o mais alto
  // quando ela termina mexe no loading/estado — isso é inequívoco (ao
  // contrário de comparar por `contextId`, que pode repetir entre duas
  // chamadas distintas para o mesmo contexto, ex: force refresh) e garante
  // que o loading SEMPRE converge pra `false`: a última chamada disparada,
  // não importa quantas vierem depois de outras, eventualmente termina e
  // vence essa comparação.
  const approvedPostsGenerationRef = useRef(0);

  const loadApprovedPosts = useCallback(
    (forceRefresh: boolean = false): Promise<void> => {
      const requestContextId = contextIdRef.current;
      if (
        !forceRefresh &&
        approvedPostsLoadedContextRef.current === requestContextId
      ) {
        return Promise.resolve();
      }
      if (
        !forceRefresh &&
        approvedPostsRequestRef.current?.contextId === requestContextId
      ) {
        return approvedPostsRequestRef.current.promise;
      }

      const myGeneration = ++approvedPostsGenerationRef.current;

      const promise = (async () => {
        setApprovedPostsLoading(true);
        try {
          const [approvedRes, publishedRes] = await Promise.all([
            postsService.getApprovedPosts(forceRefresh, 1, 100),
            postsService.getPublishedPosts(forceRefresh, 1, 100),
          ]);
          // Não escreve o resultado se o contexto mudou nesse meio-tempo
          // (dados seriam do org/empresa-cliente errado) — só o loading (no
          // finally, abaixo) ignora o contexto e olha só a geração.
          if (
            !isMountedRef.current ||
            contextIdRef.current !== requestContextId
          )
            return;
          const merged = [
            ...(Array.isArray(approvedRes?.posts) ? approvedRes.posts : []),
            ...(Array.isArray(publishedRes?.posts) ? publishedRes.posts : []),
          ];
          const byId = new Map<string, ApprovedPost>();
          for (const p of merged) byId.set(String(p.id), p as any);
          setApprovedPosts(Array.from(byId.values()));
          approvedPostsLoadedContextRef.current = requestContextId;
        } catch {
          if (isMountedRef.current && contextIdRef.current === requestContextId)
            setApprovedPosts([]);
        } finally {
          if (
            isMountedRef.current &&
            approvedPostsGenerationRef.current === myGeneration
          ) {
            setApprovedPostsLoading(false);
          }
          if (approvedPostsRequestRef.current?.contextId === requestContextId) {
            approvedPostsRequestRef.current = null;
          }
        }
      })();

      approvedPostsRequestRef.current = {
        contextId: requestContextId,
        promise,
      };
      return promise;
    },
    [],
  );

  // Ao trocar de contexto (org/empresa-cliente), os posts já carregados
  // pertencem ao contexto anterior — só invalida (sem refetch automático
  // daqui, que já causou reinícios em cascata quando o contexto resolve em
  // múltiplas etapas assíncronas logo no boot). Quem precisar dos dados de
  // novo (mount do Step 2, ou o prefetch abaixo) chama `loadApprovedPosts`
  // normalmente e o guard acima deixa passar por não bater mais o contextId.
  useEffect(() => {
    if (
      approvedPostsLoadedContextRef.current &&
      approvedPostsLoadedContextRef.current !== contextId
    ) {
      approvedPostsLoadedContextRef.current = null;
      setApprovedPosts([]);
    }
  }, [contextId]);

  // Prefetch em background: enquanto o usuário ainda está no Step 1, adianta
  // (sem bloquear a UI) a busca dos posts que o Step 2 vai precisar, usando
  // idle time do navegador. Se o usuário chegar ao Step 2 antes do prefetch
  // terminar, o guard de `loadApprovedPosts` evita uma segunda requisição —
  // o Step 2 só aguarda o mesmo request já em voo.
  useEffect(() => {
    if (currentStep !== 1) return;
    const win = window as typeof window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (win.requestIdleCallback) {
      const idleId = win.requestIdleCallback(
        () => void loadApprovedPosts(false),
        {
          timeout: 4000,
        },
      );
      return () => win.cancelIdleCallback?.(idleId);
    }
    const timeoutId = window.setTimeout(
      () => void loadApprovedPosts(false),
      1500,
    );
    return () => window.clearTimeout(timeoutId);
  }, [currentStep, loadApprovedPosts]);

  const selectedApprovedPost = useMemo(
    () =>
      approvedPosts.find((p) => String(p.id) === String(approvedPostId)) ||
      null,
    [approvedPosts, approvedPostId],
  );

  const applyApprovedPostToAd = useCallback(
    (post?: ApprovedPost) => {
      const target = post || selectedApprovedPost;
      if (!target) return;

      if (post && String(post.id) !== approvedPostId) {
        setApprovedPostId(String(post.id));
      }

      // --- Preenchimento automático a partir do post (lógica local,
      // baseada em regex/heurísticas de texto — não usa IA/LLM) ---
      const suggestions = suggestAdContentFromPost(target, platform);

      setHeadline(suggestions.headline);
      setDescription(suggestions.description);
      setMessage(suggestions.message);

      // --- Sincronização de Mídia ---
      const media = extractPostMediaUrl(target);
      const allMedia = extractPostMediaUrls(target);
      console.log("📸 [ADS CONTEXT] Extracted media for ad sync:", {
        media,
        allMedia,
      });
      setPostMediaUrls(allMedia);

      if (media && media.url) {
        if (platform === "google_ads") {
          setGooglePmaxImageUrl(media.url);
        }
      }

      // Detecção automática de link de destino
      const ctaUrl =
        extractFirstUrl(target.call_to_action) ||
        extractFirstUrl(target.content);
      if (ctaUrl) {
        if (isWhatsappUrl(ctaUrl)) {
          setDestinationType("whatsapp");
          setLinkUrl(ctaUrl);
          const phone = extractWhatsappPhoneFromUrl(ctaUrl);
          if (phone) setWhatsappPhone(phone);
        } else {
          setDestinationType("website");
          setLinkUrl(ctaUrl);
        }
      }

      showToastSuccess(t("Anúncio preenchido com base no post!"));
    },
    [
      selectedApprovedPost,
      approvedPostId,
      platform,
      googleCampaignType,
      setApprovedPostId,
      setMetaPlatform,
      setMessage,
      setHeadline,
      setDescription,
      setDestinationType,
      setLinkUrl,
      setWhatsappPhone,
      setWhatsappPrefill,
      setGooglePmaxImageUrl,
      t,
    ],
  );

  // --- API Handlers ---
  const handleSuggestTargeting = async () => {
    setSuggesting(true);
    try {
      const baseEndpoint =
        platform === "meta"
          ? "/api/ads/meta/targeting-suggestion"
          : platform === "chatgpt_ads"
            ? "/api/ads/chatgpt/targeting-suggestion"
            : "/api/ads/google/targeting-suggestion";
      const params = new URLSearchParams();
      if (message?.trim()) params.set("message", message.trim());
      if (headline?.trim()) params.set("headline", headline.trim());
      if (businessName?.trim())
        params.set("business_name", businessName.trim());
      const endpoint = params.toString()
        ? `${baseEndpoint}?${params}`
        : baseEndpoint;
      const data = await apiService.get(endpoint);
      if (data.success && data.targeting) setSuggestedTargeting(data.targeting);
    } catch {
      showToastError(t("Erro ao buscar sugestão de IA"));
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestedTargeting = () => {
    if (!suggestedTargeting) return;
    if (platform === "chatgpt_ads") {
      if (suggestedTargeting.context_hints) {
        const merged = [
          ...contextHints,
          ...suggestedTargeting.context_hints.filter(
            (h: string) => !contextHints.includes(h),
          ),
        ];
        setContextHints(merged);
      }
      showToastSuccess(t("Sugestão aplicada!"));
      return;
    }
    setAgeMin(String(suggestedTargeting.age_min || 25));
    setAgeMax(String(suggestedTargeting.age_max || 45));
    setGender(suggestedTargeting.gender || "all");
    if (platform === "meta") {
      if (suggestedTargeting.interests)
        setInterests(suggestedTargeting.interests);
      if (suggestedTargeting.custom_locations)
        setCustomLocations(suggestedTargeting.custom_locations);
    } else {
      if (suggestedTargeting.keywords) setKeywords(suggestedTargeting.keywords);
    }
    showToastSuccess(t("Sugestão aplicada!"));
  };

  // Só é relevante no Step 3 (seletor de conta LinkedIn em AudienceProfile).
  // Mesmo guard por contexto usado em `loadApprovedPosts`: evita refetch ao
  // reentrar no Step 3 ou ao alternar a plataforma para longe/de volta pro
  // LinkedIn, mas recarrega se a org/empresa-cliente ativa mudar. `[]` como
  // dependência (lendo `selectedLinkedinAccount` via ref) mantém a função
  // estável — antes ela mudava de identidade a cada auto-seleção de conta,
  // o que causava um segundo fetch redundante pelo efeito que a chamava.
  const linkedinAccountsLoadedContextRef = useRef<string | null>(null);
  const linkedinAccountsRequestRef = useRef<{
    contextId: string;
    promise: Promise<void>;
  } | null>(null);
  // Ver comentário em `approvedPostsGenerationRef` — mesmo raciocínio: só a
  // chamada com o número mais alto quando termina mexe no loading/estado.
  const linkedinAccountsGenerationRef = useRef(0);
  const selectedLinkedinAccountRef = useRef(selectedLinkedinAccount);
  selectedLinkedinAccountRef.current = selectedLinkedinAccount;

  const loadLinkedinAccounts = useCallback(
    (forceRefresh: boolean = false): Promise<void> => {
      const requestContextId = contextIdRef.current;
      if (
        !forceRefresh &&
        linkedinAccountsLoadedContextRef.current === requestContextId
      ) {
        return Promise.resolve();
      }
      if (
        !forceRefresh &&
        linkedinAccountsRequestRef.current?.contextId === requestContextId
      ) {
        return linkedinAccountsRequestRef.current.promise;
      }

      const myGeneration = ++linkedinAccountsGenerationRef.current;

      const promise = (async () => {
        setLoadingLinkedinAccounts(true);
        try {
          const data = await apiService.get("/api/ads/linkedin/accounts");
          // Não escreve o resultado se o contexto mudou nesse meio-tempo —
          // só o loading (no finally, abaixo) ignora o contexto.
          if (
            !isMountedRef.current ||
            contextIdRef.current !== requestContextId
          )
            return;
          if (data.success) {
            setLinkedinAccounts(data.accounts);
            if (
              data.accounts.length > 0 &&
              !selectedLinkedinAccountRef.current
            ) {
              const virtusAccount = data.accounts.find((acc: any) =>
                acc.name.toLowerCase().includes("virtus"),
              );
              setSelectedLinkedinAccount(
                virtusAccount ? virtusAccount.id : data.accounts[0].id,
              );
            }
            linkedinAccountsLoadedContextRef.current = requestContextId;
          } else {
            setLinkedinAccounts([]);
            if (
              isMountedRef.current &&
              contextIdRef.current === requestContextId
            )
              showToastError(
                data.error || t("Falha ao carregar contas do LinkedIn."),
              );
          }
        } catch (err) {
          console.error("Error loading LinkedIn accounts:", err);
          if (
            isMountedRef.current &&
            contextIdRef.current === requestContextId
          ) {
            setLinkedinAccounts([]);
            showToastError(t("Falha ao carregar contas do LinkedIn."));
          }
        } finally {
          if (
            isMountedRef.current &&
            linkedinAccountsGenerationRef.current === myGeneration
          ) {
            setLoadingLinkedinAccounts(false);
          }
          if (
            linkedinAccountsRequestRef.current?.contextId === requestContextId
          ) {
            linkedinAccountsRequestRef.current = null;
          }
        }
      })();

      linkedinAccountsRequestRef.current = {
        contextId: requestContextId,
        promise,
      };
      return promise;
    },
    [],
  );

  // Invalida (sem refetch automático daqui — ver comentário equivalente em
  // `loadApprovedPosts`) ao trocar de org/empresa-cliente. Quem precisar das
  // contas de novo (próxima entrada no Step 3) dispara o fetch.
  useEffect(() => {
    if (
      linkedinAccountsLoadedContextRef.current &&
      linkedinAccountsLoadedContextRef.current !== contextId
    ) {
      linkedinAccountsLoadedContextRef.current = null;
      setLinkedinAccounts([]);
    }
  }, [contextId]);

  const handleGeocodeSearch = async () => {
    if (!searchTerms.trim()) return;
    setSearchingGeocode(true);
    try {
      const resp = await apiService.get("/api/ads/geocode", {
        address: searchTerms,
      });
      if (resp.success && resp.results) setGeocodeResults(resp.results);
    } finally {
      setSearchingGeocode(false);
    }
  };

  // LinkedIn Reach Estimate Effect
  useEffect(() => {
    if (platform !== "linkedin" || !selectedLinkedinAccount) return;

    const fetchEstimate = async () => {
      setLoadingEstimate(true);
      setReachEstimateError(false);
      try {
        const targeting = {
          locations: customLocations.map((l) => l.urn || l.name), // Fallback to name if urn missing
          jobTitles: linkedinJobTitles.map((t) => t.urn),
          industries: linkedinIndustries.map((i) => i.urn),
          seniorities: linkedinSeniorities.map((s) => s.urn),
          language: linkedinLanguage,
        };

        const data = await apiService.post("/api/ads/linkedin/reach-estimate", {
          account_urn: selectedLinkedinAccount,
          targeting,
          budget: parseFloat(dailyBudget.replace(",", ".")) || 0,
        });

        if (data.success) {
          setReachEstimate(data.estimate);
        } else {
          setReachEstimateError(true);
        }
      } catch (err) {
        console.error("Erro ao carregar estimativa LinkedIn:", err);
        setReachEstimateError(true);
      } finally {
        setLoadingEstimate(false);
      }
    };

    const timer = setTimeout(fetchEstimate, 1000);
    return () => clearTimeout(timer);
  }, [
    platform,
    selectedLinkedinAccount,
    linkedinJobTitles,
    linkedinIndustries,
    linkedinSeniorities,
    linkedinLanguage,
    dailyBudget,
    customLocations,
  ]);

  const addCustomLocation = (res: any) => {
    const name = res.display_name.split(",")[0];
    const newLoc = {
      latitude: parseFloat(res.lat),
      longitude: parseFloat(res.lon),
      radius: parseInt(radiusKm) || 5,
      distance_unit: "kilometer",
      name,
    };
    setCustomLocations([...customLocations, newLoc]);

    // Sincronizar com Google Ads se estiver ativo
    if (platform === "google_ads") {
      setLocations([...locations, { type: "city", value: name }]);
    }

    setGeocodeResults([]);
    setSearchTerms("");
  };

  const addKeyword = (value?: string) => {
    const kw = (value || "").trim();
    if (kw && !keywords.includes(kw)) setKeywords([...keywords, kw]);
  };
  const removeKeyword = (idx: number) =>
    setKeywords(keywords.filter((_, i) => i !== idx));

  const optimizeKeywordsWithTrends = async () => {
    if (keywords.length === 0) {
      showToastError(
        t(
          "Adicione pelo menos 1 palavra-chave para analisar no Google Trends.",
        ),
      );
      return;
    }
    setTrendsLoading(true);
    try {
      const data = await apiService.post("/api/ads/google/trends/keywords", {
        keywords,
        geo: "BR",
      });
      const ranked = Array.isArray(data.ranked) ? data.ranked : [];
      const newKeywords = ranked.map((r: any) => r.keyword).filter(Boolean);
      if (newKeywords.length > 0) {
        setKeywords(newKeywords);
        showToastSuccess(t("Keywords otimizadas!"));
      } else {
        showToastError(t("Não foi possível otimizar as keywords."));
      }
    } finally {
      setTrendsLoading(false);
    }
  };

  const createMetaLookalike = useCallback(async () => {
    setCreatingLookalike(true);
    try {
      const response = await apiService.post(
        "/api/ads/meta/audiences/lookalike",
        {
          seed_source: metaLookalikeSource,
          percent: Number(metaLookalikePercent),
          country: "BR",
          event_name:
            metaLookalikeSource === "pixel_event"
              ? metaLookalikeEvent
              : undefined,
          retention_days:
            metaLookalikeSource === "pixel_event" ? 30 : undefined,
          name_prefix: "Plataforma",
        },
      );

      if (response.success && response.data?.lookalike_id) {
        setMetaLookalikeAudienceId(String(response.data.lookalike_id));
        showToastSuccess(t("Lookalike criada com sucesso!"));
      } else throw new Error(response.error);
    } catch (e: any) {
      let errorMsg = e.message || t("Erro ao criar Lookalike");

      // Se for um erro do Facebook em formato JSON, tentar extrair a mensagem amigável
      if (typeof e.response?.data?.error === "object") {
        const fbError = e.response.data.error;
        errorMsg = fbError.error_user_msg || fbError.message || errorMsg;
      }

      showToastError(errorMsg);
    } finally {
      setCreatingLookalike(false);
    }
  }, [metaLookalikeSource, metaLookalikePercent, metaLookalikeEvent]);

  const createMetaCustomAudience = useCallback(async () => {
    setCreatingCustomAudience(true);
    try {
      const response = await apiService.post(
        "/api/ads/meta/audiences/create-custom",
        {
          audience_name: metaCustomAudienceName,
        },
      );

      if (response.success && response.audience_id) {
        setMetaCustomAudienceId(String(response.audience_id));
        showToastSuccess(t("Público Personalizado criado com sucesso!"));
      } else throw new Error(response.error);
    } catch (e: any) {
      let errorMsg = e.message || t("Erro ao criar Público");
      if (typeof e.response?.data?.error === "object") {
        const fbError = e.response.data.error;
        errorMsg = fbError.error_user_msg || fbError.message || errorMsg;
      } else if (typeof e.response?.data?.error === "string") {
        errorMsg = e.response.data.error;
      }
      showToastError(errorMsg);
    } finally {
      setCreatingCustomAudience(false);
    }
  }, [metaCustomAudienceName]);

  const fetchMetaCustomAudiences = useCallback(async () => {
    setFetchingCustomAudiences(true);
    try {
      const response = await apiService.get(
        "/api/ads/meta/audiences/list-custom",
      );
      if (response.success) {
        setMetaCustomAudiencesList(response.audiences || []);
      } else {
        showToastError(
          response.error || t("Falha ao buscar públicos personalizados."),
        );
      }
    } catch (e: any) {
      console.error("Erro ao buscar custom audiences da Meta", e);
      showToastError(t("Falha ao buscar públicos personalizados."));
    } finally {
      setFetchingCustomAudiences(false);
    }
  }, [t]);

  const createGoogleCustomerMatch = useCallback(async () => {
    setCreatingCustomerMatch(true);
    try {
      const response = await apiService.post(
        "/api/ads/google/audiences/customer-match",
        {
          source: googleCustomerMatchSource,
        },
      );

      if (response.success && response.data?.user_list_resource_name) {
        setGoogleCustomerMatchUserListResource(
          String(response.data.user_list_resource_name),
        );
        showToastSuccess(t("Customer Match sincronizada!"));
      } else throw new Error(response.error);
    } catch (e: any) {
      const errorData = e.response?.data;
      const errorMsg =
        errorData?.error ||
        errorData?.message ||
        e.message ||
        t("Erro ao criar Customer Match");

      showToastError(errorMsg);
      logger.error("Failed to create Google Customer Match", e);
    } finally {
      setCreatingCustomerMatch(false);
    }
  }, [googleCustomerMatchSource]);

  const addCustomField = () =>
    setCustomFields([
      ...customFields,
      { id: `custom_${Date.now()}`, label: "", type: "text", required: false },
    ]);
  const updateCustomField = (id: string, updates: Partial<CustomField>) => {
    setCustomFields(
      customFields.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };
  const removeCustomField = (id: string) =>
    setCustomFields(customFields.filter((f) => f.id !== id));

  const handleLaunchCampaign = useCallback(
    async (e?: React.FormEvent | React.MouseEvent) => {
      if (e) e.preventDefault();
      if (loading || !isLastStep) return;

      // Limpeza robusta do orçamento
      const cleanedBudget = String(dailyBudget)
        .replace(/[^\d.,]/g, "")
        .replace(",", ".");
      const dailyBudgetNum = parseFloat(cleanedBudget);
      if (!Number.isFinite(dailyBudgetNum) || dailyBudgetNum <= 0) {
        showToastError(t("Informe um orçamento diário válido."));
        return;
      }

      const fullState = {
        platform,
        metaPlatform,
        googleCampaignType,
        name,
        dailyBudget,
        budgetMode,
        costPerResultGoal,
        linkUrl,
        message,
        headline,
        description,
        ctaText,
        trackingParam,
        destinationType,
        whatsappPhone,
        whatsappPrefill,
        whatsappOfficial,
        privacyPolicyUrl,
        startDate,
        endDate,
        startTime,
        endTime,
        ageMin,
        ageMax,
        gender,
        country,
        states,
        cities,
        interests,
        radiusKm,
        customLocations,
        locations,
        keywords,
        googlePmaxImageUrl,
        leadFields,
        customFields,
        approvedPostId,
        useMetaLookalike,
        metaLookalikeSource,
        metaLookalikePercent,
        metaLookalikeEvent,
        metaLookalikeAudienceId,
        useGoogleCustomerMatch,
        googleCustomerMatchSource,
        googleCustomerMatchUserListResource,
        sitelinks,
        businessName,
        youtubeUrls,
        postMediaUrls,
        linkedinMatchedAudiences,
        contextHints,
        chatgptObjective,
      };

      const periodDays =
        startDate && endDate
          ? Math.max(
              1,
              Math.ceil(
                (new Date(endDate).getTime() - new Date(startDate).getTime()) /
                  (1000 * 60 * 60 * 24),
              ),
            )
          : 1;
      const isChatgptAds = platform === "chatgpt_ads";
      const currencyPrefix = isChatgptAds ? "US$" : "R$";
      const totalInvest = (dailyBudgetNum * periodDays).toLocaleString(
        isChatgptAds ? "en-US" : "pt-BR",
        { style: "currency", currency: isChatgptAds ? "USD" : "BRL" },
      );

      const summaryHtml = `
            <div class="text-left font-sans space-y-4 py-4">
                <div class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-4">
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <div class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">${t("Canais")}</div>
                            <div class="text-xs font-bold text-gray-800">${platform === "meta" ? `${getPlatformName("meta")} (${getPlatformName("facebook")}/${getPlatformName("instagram")})` : getPlatformName(platform)}</div>
                        </div>
                        <div>
                            <div class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">${t("Objetivo")}</div>
                            <div class="text-xs font-bold text-gray-800">${platform === "linkedin" ? (linkedinObjective === "awareness" ? t("Conhecimento de Marca") : linkedinObjective === "leads" ? t("Geração de Leads") : linkedinObjective === "conversion" ? t("Conversões") : linkedinObjective === "traffic" ? t("Tráfego") : t("Engajamento")) : isChatgptAds ? (chatgptObjective === "conversions" ? t("Conversões") : t("Cliques")) : destinationType === "whatsapp" ? t("Lead via WhatsApp") : destinationType === "form" ? t("Lead via Formulário") : t("Tráfego Site")}</div>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200/50">
                        <div>
                            <div class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">${t("Verba Diária")}</div>
                            <div class="text-sm font-black text-purple-600">${currencyPrefix} ${dailyBudgetNum.toFixed(2)}</div>
                        </div>
                        <div>
                            <div class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">${t("Invest. Total ({days}d)", { days: periodDays })}</div>
                            <div class="text-sm font-black text-indigo-600">${totalInvest}</div>
                        </div>
                    </div>
                </div>
            </div>
        `;

      const result = await Swal.fire({
        title: t("Revisar e Lançar"),
        html: summaryHtml,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("Lançar Agora"),
        cancelButtonText: t("Revisar"),
        confirmButtonColor: "#6366f1",
        customClass: { popup: "rounded-4xl" },
      });

      if (!result.isConfirmed) return;

      setLoading(true);
      try {
        if (platform === "meta" && dailyBudgetNum < MIN_META_DAILY_BUDGET_BRL) {
          showToastError(
            t("Orçamento diário mínimo para Meta: R$ {min}.", {
              min: MIN_META_DAILY_BUDGET_BRL.toFixed(2),
            }),
          );
          return;
        }
        if (platform === "meta" && !approvedPostId) {
          showToastError(t("Selecione um post aprovado."));
          return;
        }
        if (
          platform === "chatgpt_ads" &&
          dailyBudgetNum < MIN_CHATGPT_DAILY_BUDGET_USD
        ) {
          showToastError(
            t("Orçamento mínimo diário para ChatGPT Ads: US$ {min}.", {
              min: MIN_CHATGPT_DAILY_BUDGET_USD.toFixed(2),
            }),
          );
          return;
        }
        if (platform === "chatgpt_ads" && !approvedPostId) {
          showToastError(
            t("Selecione um post aprovado para fornecer a imagem do anúncio."),
          );
          return;
        }
        if (platform === "chatgpt_ads" && !linkUrl.trim()) {
          showToastError(t("Informe a URL de destino do anúncio."));
          return;
        }

        let body: any;
        if (platform === "meta") {
          const media: any = selectedApprovedPost
            ? extractPostMediaUrl(selectedApprovedPost)
            : { url: "", type: "image", thumbnail: "" };
          body = {
            platform: metaPlatform,
            name,
            daily_budget_brl: dailyBudgetNum,
            budget_mode: budgetMode.toLowerCase(),
            cost_per_result_goal: costPerResultGoal
              ? parseFloat(costPerResultGoal.replace(",", "."))
              : undefined,
            destination_type: destinationType,
            performance_goal: performanceGoal,
            objective:
              destinationType === "form"
                ? "LEADS"
                : destinationType === "calls"
                  ? "TRAFFIC"
                  : ["whatsapp", "messenger", "instagram_direct"].includes(
                        destinationType,
                      )
                    ? "ENGAGEMENT"
                    : "TRAFFIC",
            link_url:
              destinationType === "whatsapp"
                ? linkUrl.trim() || computedWhatsappUrl || undefined
                : destinationType === "calls"
                  ? undefined
                  : linkUrl.trim() || undefined,
            whatsapp_phone:
              destinationType === "whatsapp" ? whatsappPhone : undefined,
            whatsapp_message:
              destinationType === "whatsapp" ? whatsappPrefill : undefined,
            whatsapp_official:
              destinationType === "whatsapp" ? whatsappOfficial : undefined,
            phone_number:
              destinationType === "calls" ? whatsappPhone : undefined,
            privacy_policy_url:
              destinationType === "form" ? privacyPolicyUrl.trim() : undefined,
            message,
            headline,
            description,
            approved_post_id: approvedPostId
              ? Number(approvedPostId)
              : undefined,
            cta_type: ctaText || undefined,
            url_tags: trackingParam || undefined,
            image_url:
              media.type === "image"
                ? media.url.trim() || undefined
                : media.thumbnail && media.thumbnail !== media.url
                  ? media.thumbnail.trim()
                  : undefined,
            video_url:
              media.type === "video"
                ? media.url.trim() || undefined
                : undefined,
            lookalike_audience_id:
              useMetaLookalike && metaLookalikeAudienceId
                ? metaLookalikeAudienceId
                : undefined,
            custom_audience_id:
              useMetaCustomAudience && metaCustomAudienceId
                ? metaCustomAudienceId
                : undefined,
            start_date: startDate,
            end_date: endDate,
            start_time: startTime,
            end_time: endTime, // Envia o horário separadamente para facilitar o parse
            age_min: Number(ageMin),
            age_max: Number(ageMax),
            gender,
            country,
            draft_data: fullState,
            states: states.length > 0 ? states : undefined,
            radius_km: radiusKm ? Number(radiusKm) : undefined,
            custom_locations:
              customLocations.length > 0
                ? customLocations.map((cl) => ({
                    latitude: cl.latitude,
                    longitude: cl.longitude,
                    radius: cl.radius,
                    distance_unit: cl.distance_unit,
                  }))
                : undefined,
            interests: interests.length > 0 ? interests : undefined,
            lead_form:
              destinationType === "form"
                ? {
                    fields: leadFields,
                    custom_fields:
                      customFields.length > 0 ? customFields : undefined,
                  }
                : undefined,
          };
        } else if (platform === "linkedin") {
          // Auto-create LinkedIn Lead Gen Form if name is provided but URN is not set
          let effectiveFormUrn = linkedinLeadFormUrn;
          if (
            linkedinObjective === "leads" &&
            linkedinFormName.trim() &&
            !effectiveFormUrn
          ) {
            try {
              const fields =
                leadFields.length > 0
                  ? leadFields.map((f) => f.toUpperCase())
                  : ["EMAIL", "FIRST_NAME", "LAST_NAME"];
              const customQuestions = customFields
                .filter((f) => f.label.trim())
                .map((f) => ({
                  label: f.label,
                  required: f.required ?? false,
                }));
              const formRes: any = await apiService.post(
                "/api/ads/linkedin/leadforms",
                {
                  account_urn: selectedLinkedinAccount,
                  name: linkedinFormName.trim(),
                  headline: linkedinFormHeadline.trim() || undefined,
                  description: linkedinFormDescription.trim() || undefined,
                  privacy_policy_url: privacyPolicyUrl.trim(),
                  cta_type: linkedinFormCta,
                  fields,
                  custom_questions:
                    customQuestions.length > 0 ? customQuestions : undefined,
                },
              );
              if (formRes.success && formRes.form_urn) {
                effectiveFormUrn = formRes.form_urn;
                setLinkedinLeadFormUrn(formRes.form_urn);
              } else {
                showToastError(
                  formRes.error || t("Erro ao criar formulário LinkedIn"),
                );
                return;
              }
            } catch {
              showToastError(t("Erro ao criar formulário LinkedIn"));
              return;
            }
          }

          body = {
            name,
            platform: "linkedin",
            objective: linkedinObjective,
            budget: {
              amount: dailyBudgetNum,
              currency: "BRL",
              daily_limit: dailyBudgetNum,
            },
            target_audience: {
              locations: customLocations.map((l) => l.urn || l.name),
              job_titles: linkedinJobTitles.map((t) => t.urn),
              industries: linkedinIndustries.map((i) => i.urn),
              seniorities: linkedinSeniorities.map((s) => s.urn),
              company_sizes: linkedinCompanySizes.map((s) => s.urn),
              skills: linkedinSkills.map((s) => s.urn),
              job_functions: linkedinJobFunctions.map((s) => s.urn),
            },
            account_urn: selectedLinkedinAccount,
            approved_post_id: approvedPostId
              ? Number(approvedPostId)
              : undefined,
            draft_data: fullState,
            destination_type: destinationType,
            link_url:
              destinationType === "whatsapp"
                ? computedWhatsappUrl
                : linkUrl.trim() || undefined,
            cta_type: ctaText || undefined,
            headline: headline || message,
            description: description || message,
            message: message,
            lead_form_urn: effectiveFormUrn || undefined,
            image_url:
              postMediaUrls.find((m) => m.type === "image")?.url || undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            start_time: startTime || undefined,
            end_time: endTime || undefined,
          };
        } else if (platform === "chatgpt_ads") {
          const media = selectedApprovedPost
            ? extractPostMediaUrl(selectedApprovedPost)
            : { url: "", type: "image", thumbnail: "" };
          body = {
            name,
            // A Advertiser API só aceita orçamento TOTAL (lifetime_spend_limit_micros),
            // sem orçamento diário recorrente — então convertemos o valor diário (USD)
            // informado no wizard pelo total do período (mesma conta já usada no
            // resumo "Invest. Total" mostrado ao usuário no diálogo de confirmação).
            budget: dailyBudgetNum * periodDays,
            context_hints: contextHints,
            title: headline || name,
            body: message,
            target_url: linkUrl.trim(),
            image_url:
              (media.type === "image" ? media.url : media.thumbnail) || "",
            objective: chatgptObjective,
            post_id: approvedPostId ? Number(approvedPostId) : undefined,
            start_paused: false,
            draft_data: fullState,
          };
        } else {
          const allMedia = selectedApprovedPost
            ? extractPostMediaUrls(selectedApprovedPost)
            : [];
          const allImageUrls = allMedia
            .filter((m) => m.type === "image")
            .map((m) => m.url);
          const firstImage = allImageUrls[0] || "";

          body = {
            name,
            daily_budget_brl: dailyBudgetNum,
            campaign_type: googleCampaignType,
            destination_type: destinationType,
            link_url:
              destinationType === "whatsapp" ? computedWhatsappUrl : linkUrl,
            cta_type: ctaText || undefined,
            headline: headline || message,
            description: description || message,
            image_url:
              googleCampaignType === "performance_max"
                ? googlePmaxImageUrl.trim() || firstImage || undefined
                : firstImage,
            image_urls: allImageUrls.length > 0 ? allImageUrls : undefined,
            video_url: undefined, // Descontinuado em favor de youtube_video_ids
            youtube_video_ids: [
              ...youtubeUrls
                .map((url) => {
                  if (!url || !url.trim()) return null;
                  // Tenta extrair ID de 11 caracteres
                  const match = url.match(
                    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]{11})/,
                  );
                  return match ? match[1] : url.length === 11 ? url : null;
                })
                .filter((id) => id !== null),
              // Adicionar vídeo do post se houver e for do YouTube
              ...allMedia
                .filter((m) => m.type === "video")
                .map((m) => {
                  const match = m.url.match(
                    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?#]{11})/,
                  );
                  return match ? match[1] : m.url.length === 11 ? m.url : null;
                })
                .filter((id) => id !== null),
            ].filter((v, i, a) => v && a.indexOf(v) === i), // Unique and truthy
            business_name: businessName.trim() || undefined,
            customer_match_user_list_resource:
              useGoogleCustomerMatch && googleCustomerMatchUserListResource
                ? googleCustomerMatchUserListResource
                : undefined,
            approved_post_id: approvedPostId
              ? Number(approvedPostId)
              : undefined,
            start_date: startDate,
            end_date: endDate,
            start_time: startTime,
            end_time: endTime,
            age_min: Number(ageMin),
            age_max: Number(ageMax),
            gender,
            locations: locations.length > 0 ? locations : undefined,
            keywords: keywords.length > 0 ? keywords : undefined,
            sitelinks: sitelinks.some((s) => s.title.trim())
              ? sitelinks.filter((s) => s.title.trim())
              : undefined,
            lead_form:
              destinationType === "form"
                ? {
                    fields: leadFields,
                    custom_fields:
                      customFields.length > 0 ? customFields : undefined,
                  }
                : undefined,
            draft_data: fullState,
          };
        }

        const endpoint =
          platform === "meta"
            ? "/api/ads/meta/campaigns"
            : platform === "google_ads"
              ? "/api/ads/google/campaigns"
              : platform === "chatgpt_ads"
                ? "/api/ads/chatgpt/campaigns"
                : "/api/ads/campaigns";

        const data = await apiService.post(endpoint, body);
        if (data.success) {
          // Se o lançamento veio de um rascunho, promovemos para ativo/publicado.
          if (draftId) {
            try {
              const externalCampaignId =
                data?.campaign_id ||
                data?.data?.campaign_id ||
                data?.id ||
                data?.data?.id;
              const localStatus = platform === "meta" ? "paused" : "active";
              const publishSync = await apiService.post(
                `/api/ads/campaigns/${draftId}/mark-published`,
                {
                  external_campaign_id: externalCampaignId,
                  status: localStatus,
                },
              );
              if (!publishSync?.success) {
                throw new Error(
                  publishSync?.error ||
                    t("Falha ao atualizar status local da campanha."),
                );
              }
            } catch (cleanupErr) {
              // Evita falso positivo de "tudo certo" quando o lançamento não foi sincronizado localmente.
              console.warn(
                "Falha ao atualizar status do rascunho após lançamento:",
                cleanupErr,
              );
              showToastWarning(
                t(
                  "Campanha lançada na plataforma, mas o status local não foi atualizado. Tente novamente em instantes.",
                ),
              );
              return;
            }
          }

          if (data.warning) {
            showToastWarning(
              t("Campanha lançada com aviso: {warning}", {
                warning: data.warning,
              }),
            );
          } else {
            showToastSuccess(t("Campanha lançada com sucesso!"));
          }
          setCurrentStep(1);
          setName("");
          setDailyBudget("");
          setDraftId(null); // Limpa rascunho após lançar
          // Remove draftId da URL para impedir recarregamento automático do rascunho.
          const url = new URL(window.location.href);
          if (url.searchParams.has("draftId")) {
            url.searchParams.delete("draftId");
            window.history.replaceState(
              {},
              "",
              `${url.pathname}${url.search}${url.hash}`,
            );
          }
        } else throw new Error(data.error || t("Erro no lançamento"));
      } catch (e: any) {
        showToastError(e.message || t("Erro ao lançar campanha"));
      } finally {
        setLoading(false);
      }
    },
    [
      activeContext,
      ageMin,
      ageMax,
      approvedPostId,
      budgetMode,
      costPerResultGoal,
      computedWhatsappUrl,
      country,
      customFields,
      customLocations,
      dailyBudget,
      description,
      destinationType,
      draftId,
      endDate,
      startTime,
      endTime,
      gender,
      googleCampaignType,
      googleCustomerMatchUserListResource,
      headline,
      interests,
      isLastStep,
      keywords,
      leadFields,
      linkUrl,
      loading,
      message,
      metaLookalikeAudienceId,
      metaLookalikeEvent,
      metaLookalikePercent,
      metaLookalikeSource,
      metaPlatform,
      platform,
      privacyPolicyUrl,
      selectedApprovedPost,
      useGoogleCustomerMatch,
      useMetaLookalike,
      ctaText,
      youtubeUrls,
      selectedLinkedinAccount,
      linkedinJobTitles,
      linkedinIndustries,
      linkedinSeniorities,
      linkedinCompanySizes,
      linkedinSkills,
      linkedinJobFunctions,
      linkedinLanguage,
      linkedinObjective,
      linkedinLeadFormUrn,
      startDate,
      contextHints,
      chatgptObjective,
    ],
  );

  const handleSaveDraft = useCallback(async () => {
    setLoading(true);
    try {
      const cleanedBudget = String(dailyBudget)
        .replace(/[^\d.,]/g, "")
        .replace(",", ".");
      const dailyBudgetNum = parseFloat(cleanedBudget) || 0;

      const fullState = {
        platform,
        metaPlatform,
        googleCampaignType,
        name,
        dailyBudget,
        budgetMode,
        costPerResultGoal,
        linkUrl,
        message,
        headline,
        description,
        ctaText,
        trackingParam,
        destinationType,
        whatsappPhone,
        whatsappPrefill,
        whatsappOfficial,
        privacyPolicyUrl,
        startDate,
        endDate,
        startTime,
        endTime,
        ageMin,
        ageMax,
        gender,
        country,
        states,
        cities,
        interests,
        radiusKm,
        customLocations,
        locations,
        keywords,
        googlePmaxImageUrl,
        leadFields,
        customFields,
        approvedPostId,
        useMetaLookalike,
        metaLookalikeSource,
        metaLookalikePercent,
        metaLookalikeEvent,
        metaLookalikeAudienceId,
        useGoogleCustomerMatch,
        googleCustomerMatchSource,
        googleCustomerMatchUserListResource,
        sitelinks,
        businessName,
        youtubeUrls,
        postMediaUrls,
        selectedLinkedinAccount,
        linkedinJobTitles,
        linkedinIndustries,
        linkedinSeniorities,
        linkedinCompanySizes,
        linkedinSkills,
        linkedinJobFunctions,
        linkedinMatchedAudiences,
        linkedinLanguage,
        linkedinObjective,
        linkedinLeadFormUrn,
        linkedinFormName,
        linkedinFormCta,
        linkedinFormHeadline,
        linkedinFormDescription,
        contextHints,
        chatgptObjective,
      };

      console.log(
        "🖼️ [ADS CONTEXT] Preparing to save draft. Media URLs:",
        postMediaUrls,
      );

      const body = {
        id: draftId,
        user_id: user?.id || localStorage.getItem("user_id"),
        organization_id: activeContext.organization?.id,
        client_company_id: activeContext.clientCompany?.id,
        name: name || t("Rascunho Sem Nome"),
        platform: platform, // Garante que envie 'meta' ou 'google_ads', evitando 'both'
        objective:
          platform === "linkedin"
            ? linkedinObjective
            : destinationType === "form"
              ? "LEADS"
              : destinationType === "whatsapp"
                ? "ENGAGEMENT"
                : "TRAFFIC",
        budget: { amount: dailyBudgetNum, currency: "BRL" },
        target_audience: {
          demographics: { ageMin, ageMax, gender },
          interests: interests.map((i) => i.name),
          locations:
            platform === "meta"
              ? cities.map((c) => c.name)
              : platform === "linkedin"
                ? customLocations.map((l) => l.urn || l.name)
                : locations.map((l) => l.value),
          job_titles:
            platform === "linkedin"
              ? linkedinJobTitles.map((t) => t.urn)
              : undefined,
          industries:
            platform === "linkedin"
              ? linkedinIndustries.map((i) => i.urn)
              : undefined,
          seniorities:
            platform === "linkedin"
              ? linkedinSeniorities.map((s) => s.urn)
              : undefined,
          company_sizes:
            platform === "linkedin"
              ? linkedinCompanySizes.map((s) => s.urn)
              : undefined,
          skills:
            platform === "linkedin"
              ? linkedinSkills.map((s) => s.urn)
              : undefined,
          job_functions:
            platform === "linkedin"
              ? linkedinJobFunctions.map((s) => s.urn)
              : undefined,
        },
        draft_data: fullState,
        lead_form_urn:
          platform === "linkedin" ? linkedinLeadFormUrn : undefined,
        start_date: startDate,
        end_date: endDate,
        start_time: startTime,
        end_time: endTime,
      };

      const response = await apiService.post("/api/ads/campaigns/draft", body);
      if (response.success && response.campaign_id) {
        setDraftId(String(response.campaign_id));
        showToastSuccess(t("Rascunho salvo com sucesso!"));
      } else throw new Error(response.error || t("Erro ao salvar rascunho"));
    } catch (e: any) {
      showToastError(e.message || t("Erro ao salvar rascunho"));
    } finally {
      setLoading(false);
    }
  }, [
    draftId,
    activeContext,
    platform,
    metaPlatform,
    googleCampaignType,
    name,
    dailyBudget,
    budgetMode,
    costPerResultGoal,
    linkUrl,
    message,
    headline,
    description,
    ctaText,
    trackingParam,
    destinationType,
    whatsappPhone,
    whatsappPrefill,
    privacyPolicyUrl,
    startDate,
    endDate,
    ageMin,
    ageMax,
    gender,
    country,
    states,
    cities,
    interests,
    radiusKm,
    customLocations,
    locations,
    keywords,
    googlePmaxImageUrl,
    leadFields,
    customFields,
    approvedPostId,
    useMetaLookalike,
    metaLookalikeSource,
    metaLookalikePercent,
    metaLookalikeEvent,
    metaLookalikeAudienceId,
    useGoogleCustomerMatch,
    googleCustomerMatchSource,
    googleCustomerMatchUserListResource,
    sitelinks,
    businessName,
    youtubeUrls,
    postMediaUrls,
    whatsappOfficial,
    selectedLinkedinAccount,
    linkedinJobTitles,
    linkedinIndustries,
    linkedinSeniorities,
    linkedinCompanySizes,
    linkedinSkills,
    linkedinJobFunctions,
    linkedinLanguage,
    linkedinObjective,
    linkedinLeadFormUrn,
    contextHints,
    chatgptObjective,
  ]);

  // Carregar Rascunho se houver ID na URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get("draftId");

    // Se temos um ID na URL e ele é diferente do rascunho atual, carregamos
    if (id && id !== draftId) {
      const loadDraft = async () => {
        setLoading(true);
        try {
          const data = await apiService.get(`/api/ads/campaigns/${id}`);
          if (data) {
            let s = data.draft_data || {};
            if (typeof s === "string" && s.startsWith("{")) {
              try {
                s = JSON.parse(s);
              } catch (e) {
                console.error("Falha ao parsear draft_data:", e);
              }
            }

            console.log("📦 [ADS CONTEXT] Loading campaign/draft state:", s);
            setDraftId(id);

            // Fallback from main fields
            if (s.platform || data.platform)
              setPlatform(s.platform || data.platform);
            if (s.metaPlatform) setMetaPlatform(s.metaPlatform);
            if (s.googleCampaignType)
              setGoogleCampaignType(s.googleCampaignType);
            if (s.name || data.name) setName(s.name || data.name);
            // setObjective removido por ser campo derivado no frontend atual

            const budgetVal =
              s.dailyBudget || (data.budget ? data.budget.amount : "");
            if (budgetVal) setDailyBudget(String(budgetVal));
            if (s.budgetMode) setBudgetMode(s.budgetMode);
            if (s.costPerResultGoal)
              setCostPerResultGoal(String(s.costPerResultGoal));

            if (s.linkUrl) setLinkUrl(s.linkUrl);
            if (s.message) setMessage(s.message);
            if (s.headline) setHeadline(s.headline);
            if (s.description) setDescription(s.description);
            if (s.ctaText) setCtaText(s.ctaText);
            if (s.trackingParam) setTrackingParam(s.trackingParam);
            if (s.destinationType) setDestinationType(s.destinationType);

            const startVal = s.startDate || data.start_date;
            if (startVal && typeof startVal === "string") {
              setStartDate(startVal.split("T")[0]);
            } else if (startVal) {
              setStartDate(String(startVal).split(" ")[0]);
            }

            const endVal = s.endDate || data.end_date;
            if (endVal && typeof endVal === "string") {
              setEndDate(endVal.split("T")[0]);
            } else if (endVal) {
              setEndDate(String(endVal).split(" ")[0]);
            }
            if (s.destinationType) setDestinationType(s.destinationType);
            if (s.whatsappPhone) setWhatsappPhone(s.whatsappPhone);
            if (s.whatsappPrefill) setWhatsappPrefill(s.whatsappPrefill);
            if (s.whatsappOfficial !== undefined)
              setWhatsappOfficial(s.whatsappOfficial);
            if (s.privacyPolicyUrl) setPrivacyPolicyUrl(s.privacyPolicyUrl);
            if (s.startTime) setStartTime(s.startTime);
            if (s.endTime) setEndTime(s.endTime);

            const ta = data.target_audience || {};
            const ageMinVal = s.ageMin || ta.min_age;
            if (ageMinVal) setAgeMin(ageMinVal);

            const ageMaxVal = s.ageMax || ta.max_age;
            if (ageMaxVal) setAgeMax(ageMaxVal);

            const genderVal =
              s.gender ||
              (ta.genders && ta.genders.length > 0 ? ta.genders[0] : "");
            if (genderVal) setGender(genderVal);

            if (s.country) setCountry(s.country);
            if (s.states) setStates(s.states);
            if (s.cities) setCities(s.cities);

            const interestsVal = s.interests || ta.interests;
            if (interestsVal) setInterests(interestsVal);

            if (s.radiusKm) setRadiusKm(s.radiusKm);
            if (s.customLocations) setCustomLocations(s.customLocations);

            const locationsVal = s.locations || ta.locations;
            if (locationsVal) setLocations(locationsVal);

            if (s.keywords) setKeywords(s.keywords);
            if (s.googlePmaxImageUrl)
              setGooglePmaxImageUrl(s.googlePmaxImageUrl);
            if (s.leadFields) setLeadFields(s.leadFields);
            if (s.customFields) setCustomFields(s.customFields);
            if (s.businessName) setBusinessName(s.businessName);
            if (s.youtubeUrls) setYoutubeUrls(s.youtubeUrls);
            else if (s.youtubeUrl) setYoutubeUrls([s.youtubeUrl]);
            if (s.approvedPostId) setApprovedPostId(String(s.approvedPostId));
            if (s.useMetaLookalike !== undefined)
              setUseMetaLookalike(s.useMetaLookalike);
            if (s.metaLookalikeSource)
              setMetaLookalikeSource(s.metaLookalikeSource);
            if (s.metaLookalikePercent)
              setMetaLookalikePercent(s.metaLookalikePercent);
            if (s.metaLookalikeEvent)
              setMetaLookalikeEvent(s.metaLookalikeEvent);
            if (s.metaLookalikeAudienceId)
              setMetaLookalikeAudienceId(s.metaLookalikeAudienceId);
            if (s.useGoogleCustomerMatch !== undefined)
              setUseGoogleCustomerMatch(s.useGoogleCustomerMatch);
            if (s.googleCustomerMatchSource)
              setGoogleCustomerMatchSource(s.googleCustomerMatchSource);
            if (s.googleCustomerMatchUserListResource)
              setGoogleCustomerMatchUserListResource(
                s.googleCustomerMatchUserListResource,
              );
            if (s.sitelinks) setSitelinks(s.sitelinks);
            if (s.postMediaUrls) setPostMediaUrls(s.postMediaUrls);

            if (s.selectedLinkedinAccount)
              setSelectedLinkedinAccount(s.selectedLinkedinAccount);
            if (s.linkedinJobTitles) setLinkedinJobTitles(s.linkedinJobTitles);
            if (s.linkedinIndustries)
              setLinkedinIndustries(s.linkedinIndustries);
            if (s.linkedinSeniorities)
              setLinkedinSeniorities(s.linkedinSeniorities);
            if (s.linkedinCompanySizes)
              setLinkedinCompanySizes(s.linkedinCompanySizes);
            if (s.linkedinSkills) setLinkedinSkills(s.linkedinSkills);
            if (s.linkedinJobFunctions)
              setLinkedinJobFunctions(s.linkedinJobFunctions);
            if (s.linkedinMatchedAudiences)
              setLinkedinMatchedAudiences(s.linkedinMatchedAudiences);
            if (s.trackingParam) {
              setTrackingParam(s.trackingParam);
              setIsTrackingParamManual(true);
            }
            if (s.linkedinLanguage) setLinkedinLanguage(s.linkedinLanguage);
            if (s.linkedinObjective) setLinkedinObjective(s.linkedinObjective);
            if (s.linkedinLeadFormUrn)
              setLinkedinLeadFormUrn(s.linkedinLeadFormUrn);
            if (s.contextHints) setContextHints(s.contextHints);
            if (s.chatgptObjective) setChatgptObjective(s.chatgptObjective);

            // Se for rascunho carregado via URL, podemos ir pro passo 1 pra revisão
            setCurrentStep(1);
            showToastSuccess(t("Rascunho carregado com sucesso!"));
          }
        } catch (e) {
          console.error("Erro ao carregar rascunho:", e);
          showToastError(t("Não foi possível carregar o rascunho."));
        } finally {
          setLoading(false);
        }
      };
      loadDraft();
    }
  }, [draftId]); // Dependência em draftId e URL (implicitamente)

  // --- Value Object ---
  const value = {
    platform,
    setPlatform,
    currentStep,
    setCurrentStep,
    steps,
    nextStep,
    prevStep,
    isLastStep,
    canGoNext,
    charLimitIssues,
    missingFieldIssues,
    name,
    setName,
    dailyBudget,
    setDailyBudget,
    budgetMode,
    setBudgetMode,
    costPerResultGoal,
    setCostPerResultGoal,
    linkUrl,
    setLinkUrl,
    message,
    setMessage,
    headline,
    setHeadline,
    description,
    setDescription,
    ctaText,
    setCtaText,
    trackingParam,
    setTrackingParam,
    isTrackingParamManual,
    setIsTrackingParamManual,
    destinationType,
    setDestinationType,
    performanceGoal,
    setPerformanceGoal,
    whatsappPhone,
    setWhatsappPhone,
    whatsappPrefill,
    setWhatsappPrefill,
    computedWhatsappUrl,
    privacyPolicyUrl,
    setPrivacyPolicyUrl,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    ageMin,
    setAgeMin,
    ageMax,
    setAgeMax,
    gender,
    setGender,
    country,
    setCountry,
    states,
    setStates,
    cities,
    setCities,
    metaPlatform,
    setMetaPlatform,
    interests,
    setInterests,
    radiusKm,
    setRadiusKm,
    customLocations,
    setCustomLocations,
    searchTerms,
    setSearchTerms,
    geocodeResults,
    setGeocodeResults,
    searchingGeocode,
    handleGeocodeSearch,
    addCustomLocation,
    googleCampaignType,
    setGoogleCampaignType,
    locations,
    setLocations,
    keywords,
    setKeywords,
    addKeyword,
    removeKeyword,
    trendsLoading,
    optimizeKeywordsWithTrends,
    googlePmaxImageUrl,
    setGooglePmaxImageUrl,
    sitelinks,
    setSitelinks,
    updateSitelink,
    linkedinAccounts,
    loadingLinkedinAccounts,
    loadLinkedinAccounts,
    selectedLinkedinAccount,
    setSelectedLinkedinAccount,
    linkedinJobTitles,
    setLinkedinJobTitles,
    linkedinIndustries,
    setLinkedinIndustries,
    linkedinSeniorities,
    setLinkedinSeniorities,
    useMetaLookalike,

    setUseMetaLookalike,
    metaLookalikeSource,
    setMetaLookalikeSource,
    metaLookalikePercent,
    setMetaLookalikePercent,
    metaLookalikeEvent,
    setMetaLookalikeEvent,
    metaLookalikeAudienceId,
    creatingLookalike,
    createMetaLookalike,
    useMetaCustomAudience,
    setUseMetaCustomAudience,
    metaCustomAudienceName,
    setMetaCustomAudienceName,
    metaCustomAudienceId,
    setMetaCustomAudienceId,
    metaCustomAudiencesList,
    fetchingCustomAudiences,
    fetchMetaCustomAudiences,
    creatingCustomAudience,
    createMetaCustomAudience,
    useGoogleCustomerMatch,
    setUseGoogleCustomerMatch,
    googleCustomerMatchSource,
    setGoogleCustomerMatchSource,
    googleCustomerMatchUserListResource,
    creatingCustomerMatch,
    createGoogleCustomerMatch,
    approvedPosts,
    approvedPostsLoading,
    approvedPostId,
    setApprovedPostId,
    selectedApprovedPost,
    loadApprovedPosts,
    applyApprovedPostToAd,
    leadFields,
    setLeadFields,
    standardLeadFields,
    customFields,
    setCustomFields,
    addCustomField,
    updateCustomField,
    removeCustomField,
    loading,
    suggesting,
    setSuggesting,
    handleSuggestTargeting,
    applySuggestedTargeting,
    suggestedTargeting,
    handleLaunchCampaign,
    handleSaveDraft,
    draftId,
    businessName,
    setBusinessName,
    youtubeUrls,
    setYoutubeUrls,
    postMediaUrls,
    setPostMediaUrls,
    whatsappOfficial,
    setWhatsappOfficial,
    reachEstimate,
    loadingEstimate,
    reachEstimateError,
    linkedinLanguage,
    setLinkedinLanguage,
    linkedinObjective,
    setLinkedinObjective,
    linkedinCompanySizes,
    setLinkedinCompanySizes,
    linkedinSkills,
    setLinkedinSkills,
    linkedinJobFunctions,
    setLinkedinJobFunctions,
    linkedinMatchedAudiences,
    setLinkedinMatchedAudiences,
    linkedinLeadFormUrn,
    setLinkedinLeadFormUrn,
    linkedinFormName,
    setLinkedinFormName,
    linkedinFormCta,
    setLinkedinFormCta,
    linkedinFormHeadline,
    setLinkedinFormHeadline,
    linkedinFormDescription,
    setLinkedinFormDescription,
    contextHints,
    setContextHints,
    addContextHint,
    removeContextHint,
    chatgptObjective,
    setChatgptObjective,
  };

  return (
    <AdsContext.Provider value={value as any}>{children}</AdsContext.Provider>
  );
};

export const useAds = () => {
  const context = useContext(AdsContext);
  if (!context) throw new Error("useAds must be used within AdsProvider");
  return context;
};
